import { z } from 'zod';
import { claveCatalogo, nombreCatalogo } from './catalogos.js';
/**
 * Esquemas del modulo comercial y de operacion de campo.
 * Dinero SIEMPRE en enteros de centavos. Nunca float, nunca string.
 */
/** Entero no negativo en centavos. El nombre del campo lo dice y el tipo lo obliga. */
export const centavos = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const monedaIso = z
    .string()
    .regex(/^[A-Z]{3}$/)
    .default('MXN');
export const fechaIso = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/* ------------------------------- Clientes -------------------------------- */
export const clienteAlta = z.object({
    clave: claveCatalogo('cli'),
    nombre: z.string().trim().min(2).max(160),
    contacto: z.string().trim().max(120).optional(),
    correo: z.string().trim().toLowerCase().email().optional(),
    /* RFC con su patron oficial. La validacion del digito verificador entra
       cuando se facture; hoy se rechaza al menos lo que ni forma tiene. */
    rfc: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/)
        .optional(),
    activo: z.boolean().optional(),
});
export const clienteEdicion = clienteAlta.partial().omit({ clave: true });
/* ------------------------------- Contratos -------------------------------- */
export const TIPOS_CONTRATO = ['CTR_POLIZA', 'CTR_PLAN_ROLLOUT'];
export const ESTADOS_CONTRATO = [
    'CTR_VIGENTE',
    'CTR_POR_VENCER',
    'CTR_VENCIDO',
    'CTR_SUSPENDIDO',
];
export const PERIODOS_CONTRATO = ['CTR_MENSUAL', 'CTR_PROYECTO'];
const contratoCampos = z.object({
    cliente_id: z.string().uuid(),
    tipo: z.enum(TIPOS_CONTRATO),
    estado: z.enum(ESTADOS_CONTRATO).optional(),
    periodo: z.enum(PERIODOS_CONTRATO),
    inicio_el: fechaIso,
    fin_el: fechaIso,
    volumen: z.number().int().positive(),
    moneda: monedaIso.optional(),
    acumula_saldo: z.boolean().optional(),
});
export const contratoAlta = contratoCampos.refine((c) => c.fin_el >= c.inicio_el, {
    message: 'La fecha de fin no puede ser anterior a la de inicio',
});
/* El cliente de un contrato no se cambia: un contrato que cambia de titular
   es un contrato nuevo, y moverlo rompe la trazabilidad del folio. */
export const contratoEdicion = contratoCampos.partial().omit({ cliente_id: true });
/* ------------------------------- Proyectos -------------------------------- */
export const ESTADOS_PROYECTO = [
    'PRY_PLANEACION',
    'PRY_EN_EJECUCION',
    'PRY_EN_RIESGO',
    'PRY_SUSPENDIDO',
    'PRY_CERRADO',
];
const proyectoCampos = z.object({
    cliente_id: z.string().uuid(),
    contrato_id: z.string().uuid(),
    nombre: z.string().trim().min(3).max(200),
    estado: z.enum(ESTADOS_PROYECTO).optional(),
    inicio_el: fechaIso,
    fin_el: fechaIso,
    gerente: z.string().trim().max(120).optional(),
    sitios_plan: z.number().int().nonnegative().optional(),
    avance_plan_pct: z.number().min(0).max(100).optional(),
    ventana_inicio: z
        .string()
        .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/)
        .optional(),
    ventana_fin: z
        .string()
        .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/)
        .optional(),
    requiere_inventario: z.boolean().optional(),
});
export const proyectoAlta = proyectoCampos.refine((p) => p.fin_el >= p.inicio_el, {
    message: 'La fecha de fin no puede ser anterior a la de inicio',
});
export const proyectoEdicion = proyectoCampos.partial().omit({ cliente_id: true });
/* --------------------------------- Sitios --------------------------------- */
export const ESTADOS_SITIO = [
    'SITIO_ALTA',
    'SITIO_INCOMPLETO',
    'SITIO_DUPLICADO',
    'SITIO_VALIDADO',
    'SITIO_BAJA',
];
export const sitioAlta = z.object({
    cliente_id: z.string().uuid(),
    /* Determinante: como la operacion del cliente nombra al sitio. Es la llave
       de deduplicacion primaria. */
    determinante: z.string().trim().min(1).max(40),
    nombre: z.string().trim().min(2).max(200),
    direccion: z.string().trim().max(400).optional(),
    cp: z
        .string()
        .regex(/^[0-9]{5}$/)
        .optional(),
    ciudad_id: z.string().uuid().optional(),
    estado_id: z.string().uuid().optional(),
    municipio: z.string().trim().max(120).optional(),
    tipo_sitio_id: z.string().uuid().optional(),
    formato_id: z.string().uuid().optional(),
    estatus: z.enum(ESTADOS_SITIO).optional(),
});
export const sitioEdicion = sitioAlta.partial().omit({ cliente_id: true, determinante: true });
/* ----------------------------------- IDC ----------------------------------- */
export const MODALIDADES_IDC = ['IDC_FIJO', 'IDC_FLOTANTE'];
export const ESTATUS_IDC = ['IDC_ACTIVO', 'IDC_INACTIVO', 'IDC_BLOQUEADO'];
export const NIVELES_EPP = ['EPP_COMPLETO', 'EPP_PARCIAL', 'EPP_SIN'];
const idcCampos = z.object({
    clave: claveCatalogo('idc'),
    nombre: nombreCatalogo.max(120),
    curp: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[A-Z0-9]{2}$/)
        .optional(),
    tipo_identificacion_id: z.string().uuid().optional(),
    numero_identificacion: z.string().trim().max(40).optional(),
    telefono: z
        .string()
        .regex(/^\+[1-9][0-9]{7,14}$/)
        .optional(),
    direccion: z.string().trim().max(400).optional(),
    ciudad_id: z.string().uuid().optional(),
    modalidad: z.enum(MODALIDADES_IDC),
    estatus: z.enum(ESTATUS_IDC).optional(),
    epp: z.enum(NIVELES_EPP).optional(),
    /* Horas laborables propias: quien trabaja 160 no se costea como quien
       trabaja 176. Tope de 16 por dia segun la tabla de validaciones. */
    horas_mes: z.number().int().min(1).max(320).optional(),
    costo_mensual_centavos: centavos.optional(),
    moneda: monedaIso.optional(),
    tiene_resguardo: z.boolean().optional(),
});
export const idcAlta = idcCampos.refine((i) => i.modalidad !== 'IDC_FIJO' || i.costo_mensual_centavos !== undefined, { message: 'Un IDC fijo por sueldo necesita su costo mensual en centavos' });
export const idcEdicion = idcCampos.partial().omit({ clave: true });
/* --------------------- Importacion de sitios por CSV ---------------------- */
/** Las nueve columnas, en orden, como las trae el archivo del cliente. */
export const COLUMNAS_CSV_SITIOS = [
    'determinante',
    'nombre',
    'direccion',
    'cp',
    'ciudad',
    'tipo_sitio',
    'formato',
    'estado',
    'municipio',
];
//# sourceMappingURL=comercial.js.map